// src/components/PaymentForm.jsx

import styles from "../styles/PayWidget.module.css";
import { InputField } from "./InputField";
import { SubmitButton } from "./SubmitButton";
import { useEffect, useState } from "react";
import fetchBINData from "../hooks/api/openbinapi";
import payOrder from "../hooks/api/payOrder";

// Функция для форматирования суммы
const formatAmount = (amount) => {
    if (!amount) return "0.00";

    const locale = "en-US"; // Используем английский формат для разделения запятыми

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

// Алгоритм Луна для проверки валидности номера карты
const isValidCardNumber = (cardNumber) => {
    const digits = cardNumber.replace(/\D/g, "").split("").reverse();
    const checksum = digits.reduce((sum, digit, index) => {
        let num = parseInt(digit, 10);
        if (index % 2 === 1) {
            num *= 2;
            if (num > 9) num -= 9;
        }
        return sum + num;
    }, 0);
    return checksum % 10 === 0;
};

export const PaymentForm = ({ order }) => {
    const [email, setEmail] = useState("");
    const [binData, setBinData] = useState(null);
    const [isValid, setIsValid] = useState(false);
    const [isCardNumberValid, setIsCardNumberValid] = useState(true); // Для отслеживания валидности номера карты
    const [card, setCard] = useState({
        number: "",
        expiry: "",
        cvv: ""
    });

    // Определяем язык на основе валюты
    const isRussian = order?.order_currency === 643;

    // Проверка валидности карты в реальном времени
    useEffect(() => {
        const cardNumber = card.number.replace(/\s/g, "");
        const isExpiryValid = /^\d{2}\/\d{2}$/.test(card.expiry);
        const isCVVValid = card.cvv.length === 3 || card.cvv.length === 4;

        // Проверяем валидность номера карты с помощью алгоритма Луна
        const cardNumberValid = cardNumber.length >= 15 && isValidCardNumber(cardNumber);
        setIsCardNumberValid(cardNumberValid); // Обновляем состояние валидности номера карты

        // Устанавливаем состояние валидности
        setIsValid(cardNumberValid && isExpiryValid && isCVVValid);
    }, [card.number, card.expiry, card.cvv]);

    const handleCardChange = (field, value) => {
        if (field === "number") {
            let digits = value.replace(/\D/g, "").slice(0, 16);
            let formatted = digits.length === 15
                ? digits.replace(/(\d{4})(\d{6})?(\d{5})?/, (_, p1, p2, p3) => [p1, p2, p3].filter(Boolean).join(" "))
                : digits.replace(/(\d{4})/g, "$1 ").trim();

            setCard((prev) => ({ ...prev, number: formatted }));
        } else if (field === "expiry") {
            let sanitized = value.replace(/\D/g, "").slice(0, 4);
            let month = sanitized.slice(0, 2);
            let year = sanitized.slice(2);

            if (month.length === 2 && (parseInt(month, 10) < 1 || parseInt(month, 10) > 12)) return;

            let formatted = year ? `${month}/${year}` : month;
            setCard((prev) => ({ ...prev, expiry: formatted }));
        } else if (field === "cvv") {
            setCard((prev) => ({ ...prev, cvv: value.replace(/\D/g, "").slice(0, 4) }));
        } else if (field === "email") {
            setEmail(value);
        }
    };

    const handlePayClick = async () => {
        const cardNumber = card.number.replace(/\s/g, "");
        const bin = cardNumber.slice(0, 6);

        let cardScheme = isRussian ? "Неизвестная карта" : "Unknown card"; // Значение по умолчанию

        // Проверяем BIN через API
        try {
            const data = await fetchBINData(bin);
            if (data) {
                setBinData(data); // Сохраняем данные BIN
                cardScheme = data.scheme || (isRussian ? "Неизвестная карта" : "Unknown card"); // Получаем схему карты (например, Visa)
            }
        } catch (error) {
            console.error(isRussian ? "Ошибка при проверке BIN:" : "Error checking BIN:", error);
            // Продолжаем выполнение даже при ошибке
        }

        // Вывод данных о карте (например, Visa)
        console.log(isRussian ? "Тип карты:" : "Card type:", cardScheme);

        // Формируем данные для оплаты
        const requestData = {
            gateWay: {
                extId: order?.order_id,
                amount: order?.order_summ, //Math.round(parseFloat(order?.order_summ || 0) * 100),
                description: order?.order_description || (isRussian ? "Оплата заказа" : "Order Payment"),
                cardInfo: {
                    cardNumber: card.number.replace(/\s/g, ""),
                    cardHolder: "MR CARDHOLDER",
                    expirationYear: `20${card.expiry.slice(3, 5)}`,
                    expirationMonth: card.expiry.slice(0, 2),
                    CVV: card.cvv
                },
            },
            methodMode: "card_rub_w3ds",
            currency: order?.order_currency || 643,
        };

        if (email) {
            requestData.email = email;
        }

        console.log(isRussian ? "Отправка данных:" : "Sending data:", requestData);

        try {
            const response = await payOrder(requestData);
            console.log(isRussian ? "Ответ от сервера:" : "Server response:", response);

            if (response?.status === "success") {
                window.parent.postMessage('paymentSuccess', '*');
            }
        } catch (error) {
            console.error(isRussian ? "Ошибка при отправке оплаты:" : "Error processing payment:", error);
            alert(isRussian ? "Произошла ошибка при обработке платежа. Попробуйте снова." : "An error occurred while processing the payment. Please try again.");
        }
    };

    return (
        <form>
            <div className={styles.row}>
                <InputField
                    type="text"
                    placeholder={isRussian ? "Номер карты" : "Card Number"}
                    value={card.number}
                    onChange={(e) => handleCardChange("number", e.target.value)}
                    className={`${styles.cardNumberInput} ${styles.input} ${isCardNumberValid ? styles.valid : styles.invalid}`}
                />
            </div>

            {binData && <p>{binData.scheme || (isRussian ? "Неизвестная система" : "Unknown system")} - {binData.bank?.name || (isRussian ? "Неизвестный банк" : "Unknown bank")}</p>}

            <div className={styles.row}>
                <InputField
                    type="text"
                    placeholder={isRussian ? "ММ / ГГ" : "MM / YY"}
                    value={card.expiry}
                    onChange={(e) => handleCardChange("expiry", e.target.value)}
                    className={`${styles.input} ${/^\d{2}\/\d{2}$/.test(card.expiry) ? styles.valid : styles.invalid}`}
                />
                <InputField
                    type="password"
                    placeholder="CVV"
                    value={card.cvv}
                    onChange={(e) => handleCardChange("cvv", e.target.value)}
                    className={`${styles.input} ${card.cvv.length === 3 || card.cvv.length === 4 ? styles.valid : styles.invalid}`} // Обновлено
                />
            </div>

            <SubmitButton onClick={handlePayClick} disabled={!isValid} className={styles.submitButton}>
                {isRussian ? "Оплатить" : "Pay"} {order?.order_summ ? formatAmount(order.order_summ / 100) : ""} {order?.currency_symbol}
            </SubmitButton>

            <div className={styles.row} style={{ marginTop: "30px" }}>
                <InputField
                    type="email"
                    value={email || ""}
                    onChange={(e) => handleCardChange("email", e.target.value)}
                    className={`${styles.input} ${styles.emailInput}`}
                    placeholder="yourmail@mailserver.com"
                    style={{ color: email ? "black" : "transparent", textShadow: email ? "none" : "0 0 0 #000" }}
                />
            </div>
        </form>
    );
};

export default PaymentForm;



